"use client";

import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { fetchTopBrands } from "@/api-service/brands";
import { TopBrandItem, TopBrandResponse } from "@/packages/types/brands";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import NextImage from "../common/NextImage";
import { Swiper, SwiperSlide } from "swiper/react";
import { Keyboard } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import { ChevronLeft, ChevronRight } from "lucide-react";

import "swiper/css";

import { HEADER_V2_DESIGN_SYSTEM } from "../header/v2/designTokens";
import { useResolvedCountryName } from "@/packages/hooks/useResolvedCountryName";
import { CategoryCardSkeleton } from "../common/Skeletons";

const BrandImageSlider: React.FC = () => {
  const countryName = useResolvedCountryName();
  const [brands, setBrands] = useState<TopBrandItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const { fastPush } = useFastRouter();

  useEffect(() => {
    let isMounted = true;
    const loadBrands = async () => {
      try {
        setLoading(true);
        const res: TopBrandResponse = await fetchTopBrands();
        if (isMounted) {
          setBrands(res.data || []);
        }
      } catch (error: unknown) {
        console.error("Error fetching top brands:", error);
        if (isMounted) {
          toast.error("Error fetching top brands");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    loadBrands();
    return () => {
      isMounted = false;
    };
  }, []);

  const handlePrev = useCallback(() => {
    swiperInstance?.slidePrev();
  }, [swiperInstance]);

  const handleNext = useCallback(() => {
    swiperInstance?.slideNext();
  }, [swiperInstance]);

  const handleSwiperInit = useCallback((swiper: SwiperType) => {
    setSwiperInstance(swiper);
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  if (!loading && brands.length === 0) {
    return null;
  }

  const renderBrand = (item: TopBrandItem, index: number) => {
    const brandObj = item.brand;
    const brandName = brandObj?.name || "Brand";
    const hasLogo = Boolean(brandObj?.logoUrl?.trim());

    return (
      <SwiperSlide key={item.id || index} className="h-auto !overflow-visible">
        <div
          onClick={() => brandObj?.slug && fastPush(`/vendor/${brandObj.slug}`)}
          className="group/card relative w-full h-24 sm:h-28 md:h-32 bg-white border border-slate-200/80 hover:border-[var(--brand-accent)] rounded-xl p-3 sm:p-4 flex items-center justify-center cursor-pointer shadow-xs group-hover/slider:opacity-60 hover:!opacity-100 hover:!scale-108 hover:z-30 hover:shadow-xl transition-all duration-300 transform overflow-hidden"
        >
          <NextImage
            src={brandObj?.logoUrl?.trim() || "/assets/site/images/logo.png"}
            alt={
              brandName
                ? `${brandName}${countryName ? ` in ${countryName}` : ""}`
                : "Brand Logo"
            }
            fill
            sizes="(max-width: 640px) 40vw, (max-width: 1024px) 25vw, 15vw"
            className={`object-contain p-2 sm:p-3 transition-transform duration-300 ease-out group-hover/card:scale-105 ${
              !hasLogo ? "opacity-30" : ""
            }`}
            priority={index < 5}
          />

          {/* Translucent bottom name bar (ONLY shown when logo image is NOT available) */}
          {!hasLogo && (
            <div className="absolute bottom-0 left-0 right-0 w-full z-10 pointer-events-none bg-white/90 backdrop-blur-xs py-1.5 px-2 text-center border-t border-slate-100">
              <p className="text-[11px] sm:text-xs font-bold tracking-tight text-slate-900 truncate capitalize">
                {brandName}
              </p>
            </div>
          )}
        </div>
      </SwiperSlide>
    );
  };

  return (
    <section className={`w-full max-w-7xl mx-auto my-6 md:my-8 ${HEADER_V2_DESIGN_SYSTEM.spacing.containerPadding}`}>
      {loading ? (
        <div className="w-full">
          <div className="h-6 w-36 bg-slate-200 rounded mb-6 animate-pulse" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <CategoryCardSkeleton key={i} />
            ))}
          </div>
        </div>
      ) : (
        <div className="relative w-full group">
          {brands.length > 0 && (
            <h2 className={`font-semibold tracking-tight ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilyHeading} ${HEADER_V2_DESIGN_SYSTEM.typography.size20} ${HEADER_V2_DESIGN_SYSTEM.colors.primaryDarkText} mb-6 md:mb-8`}>
              Popular Brands{countryName ? ` in ${countryName}` : ""}
            </h2>
          )}

          {/* Swiper Slider Wrapper */}
          <div className="relative w-full px-1 py-3 group/slider">
            <Swiper
              modules={[Keyboard]}
              spaceBetween={14}
              slidesPerView={2.4}
              keyboard={{ enabled: true }}
              onSwiper={handleSwiperInit}
              onSlideChange={handleSlideChange}
              breakpoints={{
                480: { slidesPerView: 3.2, spaceBetween: 14 },
                640: { slidesPerView: 4.2, spaceBetween: 14 },
                768: { slidesPerView: 5.2, spaceBetween: 16 },
                1024: { slidesPerView: 6, spaceBetween: 16 },
              }}
              className="w-full !overflow-visible md:!overflow-hidden"
            >
              {brands.map((item, index) => renderBrand(item, index))}
            </Swiper>

            {/* Custom Navigation Buttons */}
            {brands.length > 3 && (
              <>
                {/* Prev Button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrev();
                  }}
                  disabled={isBeginning}
                  className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 
                    hidden md:flex items-center justify-center w-8 h-14 rounded-r-md 
                    bg-black/15 hover:bg-black/25 text-white transition-all duration-300 
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] 
                    disabled:opacity-30 disabled:pointer-events-none`}
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5 stroke-[2.5]" />
                </button>

                {/* Next Button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleNext();
                  }}
                  disabled={isEnd}
                  className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 
                    hidden md:flex items-center justify-center w-8 h-14 rounded-l-md 
                    bg-black/15 hover:bg-black/25 text-white transition-all duration-300 
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] 
                    disabled:opacity-30 disabled:pointer-events-none`}
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5 stroke-[2.5]" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default BrandImageSlider;
